import { NotificationsNone } from '@mui/icons-material';
import { Badge, Box, IconButton, Popover } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import EndPlan from './EndPlan';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';

const Messages = () => {
  const { cafe } = useAdmin();
  const ref = useRef<HTMLButtonElement>(null);
  const { openMessages, setOpenMessages } = useCafe();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = () => {
    setOpenMessages(true);
  };

  const handleClose = () => {
    setOpenMessages(false);
  };

  const hasEndPlan = useMemo(() => {
    if (cafe?.plan?.type !== 'plus' && cafe?.plan?.type !== 'pro') {
      return false;
    }

    const attentionDate = new Date(cafe.plan.lastDate || Date.now());
    attentionDate.setDate(attentionDate.getDate() - 3);

    return attentionDate.getTime() < Date.now();
  }, [cafe?.plan?.lastDate, cafe?.plan?.type]);

  const open = Boolean(anchorEl);

  const messages = useMemo(
    () =>
      [
        !cafe?.messages?.step1,
        !cafe?.messages?.step2,
        !cafe?.messages?.step3,
        !cafe?.messages?.step4,
        !cafe?.messages?.step5,
      ].filter((m) => !!m),
    [cafe?.messages?.step1, cafe?.messages?.step2, cafe?.messages?.step3, cafe?.messages?.step4, cafe?.messages?.step5]
  );

  const messagesCount = messages.length + (hasEndPlan ? 1 : 0);

  useEffect(() => {
    if (openMessages) {
      setAnchorEl(ref.current);
    } else {
      setAnchorEl(null);
    }
  }, [openMessages, setOpenMessages]);

  if (messagesCount === 0) {
    return null;
  }

  return (
    <div>
      {open && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: (t) => t.palette.grey[900],
            opacity: 0.3,
            zIndex: (t) => t.zIndex.modal - 2,
          }}
        />
      )}

      <IconButton
        onClick={handleClick}
        ref={ref}
        sx={{
          zIndex: (t) => (open ? t.zIndex.modal - 1 : undefined),
          bgcolor: (t) => (open ? t.palette.background.paper : t.palette.background.default),
          borderBottomLeftRadius: open ? 0 : undefined,
          borderBottomRightRadius: open ? 0 : undefined,
        }}
      >
        <Badge badgeContent={messagesCount} color="primary">
          <NotificationsNone />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ mx: 2 }}
      >
        <Box sx={{ maxWidth: 600, maxHeight: '80vh', m: 2 }}>
          {hasEndPlan && <EndPlan />}
          {!cafe?.messages?.step1 && <Step1 />}
          {!cafe?.messages?.step2 && <Step2 />}
          {!cafe?.messages?.step3 && <Step3 />}
          {!cafe?.messages?.step4 && <Step4 />}
          {!cafe?.messages?.step5 && <Step5 />}
          <Box sx={{ height: 1 }} />
        </Box>
      </Popover>
    </div>
  );
};

export default Messages;
